// iter418 — Quick Transfer dialog: type an account number, auto-detect
// whether it belongs to a Supplier/Employee/Partner/Customer, enter
// amount + date, post a bank payment with the right linkage in one
// click. Saves the user the multi-step "find the right form / pick the
// right person" dance.
import { cur } from '@/lib/currency';
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Search, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { localToday } from '@/lib/dateRange';

const ENTITY_LABEL = {
  supplier: 'Supplier',
  employee: 'Employee',
  partner: 'Partner',
  customer: 'Customer',
  platform: 'Delivery Platform',
  beneficiary: 'Saved Beneficiary',
};
const ENTITY_TINT = {
  supplier: 'bg-amber-100 text-amber-800 border-amber-200',
  employee: 'bg-blue-100 text-blue-800 border-blue-200',
  partner: 'bg-violet-100 text-violet-800 border-violet-200',
  customer: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  platform: 'bg-sky-100 text-sky-800 border-sky-200',
  beneficiary: 'bg-stone-100 text-stone-700 border-stone-200',
};

export function QuickTransferDialog({ open, onOpenChange, bankAccounts = [], onSuccess }) {
  const [account, setAccount] = useState('');
  const [resolved, setResolved] = useState(null);   // { found, primary, alternatives }
  const [resolving, setResolving] = useState(false);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => localToday());
  const [bankAccountId, setBankAccountId] = useState(bankAccounts[0]?.id || '');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetState = () => {
    setAccount(''); setResolved(null); setAmount(''); setReference('');
    setDate(localToday());
  };

  const handleLookup = async () => {
    if (!account || account.replace(/\s/g, '').length < 4) {
      toast.error('Type at least 4 characters');
      return;
    }
    setResolving(true);
    try {
      const r = await api.get('/banking/lookup-by-account', { params: { account } });
      setResolved(r.data);
      if (!r.data.found) {
        toast.error('No supplier / employee / partner / customer found for that account');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Lookup failed');
      setResolved(null);
    } finally {
      setResolving(false);
    }
  };

  const handleSubmit = async () => {
    if (!resolved?.found) {
      toast.error('Resolve the account first');
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!bankAccountId) {
      toast.error('Pick the paying bank account');
      return;
    }
    const m = resolved.primary;
    const PAYEE_TYPE = { supplier: 'supplier', employee: 'employee', partner: 'partner', customer: 'customer', platform: 'platform' };
    const payee_type = PAYEE_TYPE[m.entity_type] || 'other';
    setSubmitting(true);
    try {
      // Use the unified /api/bank-payments POST. The backend already
      // resolves the payee_name from id+type; we stamp the resolved
      // values so the audit trail is explicit.
      await api.post('/bank-payments', {
        direction: 'outgoing',
        payee_type,
        payee_id: m.entity_id || m.beneficiary_id || '',
        payee_name: m.name || '',
        amount: Number(amount),
        payment_date: date,
        bank_account_id: bankAccountId,
        reference: reference || `Transfer to ${m.name}`,
        notes: `Quick Transfer · resolved ${m.account_number || m.iban || account}`,
      });
      toast.success(`Transfer of ${cur()} ${Number(amount).toLocaleString()} to ${m.name} recorded`);
      onOpenChange(false);
      resetState();
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { onOpenChange(o); if (!o) resetState(); }}
    >
      <DialogContent className="max-w-lg" data-testid="quick-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Quick Bank Transfer</DialogTitle>
          <DialogDescription>
            Type the recipient&apos;s account number — we&apos;ll detect whether
            it belongs to a Supplier, Employee, Partner, Customer or Platform
            and post the transfer with the right linkage in one click.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="qt-account" className="text-xs">Account number / IBAN</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="qt-account"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
                placeholder="e.g. 0123456 or SA0380000000…"
                data-testid="qt-account-input"
                autoFocus
              />
              <Button
                onClick={handleLookup}
                disabled={resolving || !account}
                variant="outline"
                data-testid="qt-lookup-btn"
              >
                <Search size={14} className="mr-1.5" />
                {resolving ? 'Searching…' : 'Look up'}
              </Button>
            </div>
            {resolved?.found && resolved?.primary && (
              <div
                className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-start gap-2"
                data-testid="qt-resolved-banner"
              >
                <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                    {resolved.primary.name}
                    <Badge variant="outline" className={`text-[9px] uppercase ${ENTITY_TINT[resolved.primary.entity_type] || ''}`}>
                      {ENTITY_LABEL[resolved.primary.entity_type] || resolved.primary.entity_type}
                    </Badge>
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {resolved.primary.bank_name && `${resolved.primary.bank_name} · `}
                    {resolved.primary.iban || resolved.primary.account_number}
                  </div>
                  {resolved.alternatives?.length > 0 && (
                    <div className="text-[10px] text-amber-700 mt-1">
                      ⓘ {resolved.alternatives.length} other entit{resolved.alternatives.length === 1 ? 'y' : 'ies'} also use this account.
                    </div>
                  )}
                </div>
              </div>
            )}
            {resolved && !resolved.found && (
              <div className="mt-2 rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2 text-xs text-rose-700 flex items-center gap-2">
                <AlertCircle size={12} />Not found in any supplier/employee/partner/customer/platform.
              </div>
            )}
          </div>

          {resolved?.found && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="qt-amount" className="text-xs">Amount (SR)</Label>
                  <Input
                    id="qt-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="mt-1"
                    data-testid="qt-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="qt-date" className="text-xs">Date</Label>
                  <Input
                    id="qt-date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1"
                    data-testid="qt-date-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="qt-bank" className="text-xs">From bank account</Label>
                <select
                  id="qt-bank"
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                  className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                  data-testid="qt-bank-select"
                >
                  <option value="">Choose…</option>
                  {bankAccounts.map((ba) => (
                    <option key={ba.id} value={ba.id}>
                      {ba.bank_name || ba.name || 'Bank'}{ba.account_number ? ` · ${ba.account_number}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="qt-ref" className="text-xs">Reference (optional)</Label>
                <Input
                  id="qt-ref"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder={`e.g. INV-1234, March salary, etc.`}
                  className="mt-1"
                  data-testid="qt-ref-input"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="qt-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!resolved?.found || submitting}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="qt-submit"
          >
            {submitting ? 'Posting…' : <>Post transfer<ArrowRight size={12} className="ml-1.5" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QuickTransferDialog;
